import { ANALYTICS_EVENTS, analyticsService } from './analytics.service';
import type { ChatEvent } from 'src/types/chatEvent';
import type { ChatAttachment, ChatMessage } from 'src/types/chatMessage';
import { ChatMessagePayloadType, ChatMessageStatus } from 'src/types/chatMessage';
import { SupportedChatChannel } from 'src/chats/supportedChatChannel';

type MessageMeta = {
  sentAt?: string;
  hasMedia?: string | null;
  channel: SupportedChatChannel;
};

const trackedEvents = new Map<string, Set<keyof typeof ANALYTICS_EVENTS>>();
const messageMeta = new Map<string, MessageMeta>();

let usedAiAssistantForNextMessage = false;

const markTracked = (messageId: string, event: keyof typeof ANALYTICS_EVENTS) => {
  const entries = trackedEvents.get(messageId) ?? new Set<keyof typeof ANALYTICS_EVENTS>();
  if (entries.has(event)) {
    return false;
  }

  entries.add(event);
  trackedEvents.set(messageId, entries);
  return true;
};

const getMediaType = (payloadType?: ChatMessagePayloadType, attachments?: ChatAttachment[]) => {
  if (payloadType === ChatMessagePayloadType.IMAGE || payloadType === ChatMessagePayloadType.IMAGE_BUTTON) {
    return 'image';
  }

  if (payloadType === ChatMessagePayloadType.VIDEO) {
    return 'video';
  }

  if (payloadType === ChatMessagePayloadType.DOC) {
    return 'document';
  }

  if (attachments?.length) {
    const extension = attachments[0]?.filename?.split('.').pop()?.toLowerCase();
    if (extension === 'png' || extension === 'jpg' || extension === 'jpeg') return 'image';
    if (extension === 'mp4') return 'video';
    if (extension === 'pdf') return 'document';
  }

  return null;
};

const getDeliveredAt = (timestamp?: number) => (timestamp ? new Date(timestamp).toISOString() : new Date().toISOString());

export const inboxAnalytics = {
  messageSent: (message: ChatMessage) => {
    if (!markTracked(message.msgId, 'MessageSent')) {
      return;
    }

    const userSentAt = new Date().toISOString();
    const hasMedia = getMediaType(message.payloadType, message.attachments);

    messageMeta.set(message.msgId, {
      sentAt: userSentAt,
      hasMedia,
      channel: message.channel,
    });

    analyticsService.track(ANALYTICS_EVENTS.MessageSent, {
      message_id: message.msgId,
      channel: message.channel,
      has_media: hasMedia,
      user_sent_at: userSentAt,
      used_ai_assistant: usedAiAssistantForNextMessage,
    });
    usedAiAssistantForNextMessage = false;
  },
  setUsedAiAssistantForNextMessage: (value: boolean) => {
    usedAiAssistantForNextMessage = value;
  },
  ensureSentTracked: (message: ChatMessage) => {
    if (trackedEvents.get(message.msgId)?.has('MessageSent')) {
      return;
    }
    inboxAnalytics.messageSent(message);
  },
  messageSendConfirmed: (event: ChatEvent) => {
    if (!markTracked(event.msgId, 'MessageSendConfirmed')) {
      return;
    }

    const meta = messageMeta.get(event.msgId);
    const messageSentAt =
      event.messageSentAt ??
      meta?.sentAt ??
      (event.timestamp ? new Date(event.timestamp).toISOString() : null);

    analyticsService.track(ANALYTICS_EVENTS.MessageSendConfirmed, {
      message_id: event.msgId,
      message_sent_at: messageSentAt,
      channel: event.channel,
    });
  },
  messageDelivered: (event: ChatEvent) => {
    if (!markTracked(event.msgId, 'MessageDelivered')) {
      return;
    }

    const meta = messageMeta.get(event.msgId);
    const deliveredAt = event.messageDeliveredAt ?? getDeliveredAt(event.timestamp);
    const deliveryLatencyMs =
      deliveredAt && (event.messageSentAt ?? meta?.sentAt)
        ? new Date(deliveredAt).getTime() - new Date(event.messageSentAt ?? (meta?.sentAt as string)).getTime()
        : null;

    analyticsService.track(ANALYTICS_EVENTS.MessageDelivered, {
      message_id: event.msgId,
      channel: event.channel,
      message_delivered_at: deliveredAt,
      delivery_latency_ms: deliveryLatencyMs,
    });
  },
  messageFailed: (event: ChatEvent) => {
    if (!markTracked(event.msgId, 'MessageFailed')) {
      return;
    }

    analyticsService.track(ANALYTICS_EVENTS.MessageFailed, {
      message_id: event.msgId,
      channel: event.channel,
      message_failure_status: event.status ?? null,
      error_code: event.errorCode ?? null,
      error_type: event.errorType ?? null,
      error_reason: event.statusDetails ?? null,
      retry_count: event.retryCount ?? null,
    });
  },
  messageReceived: (message: ChatMessage) => {
    if (!markTracked(message.msgId, 'MessageReceived')) {
      return;
    }

    const receivedAt = message.date ? new Date(message.date).toISOString() : new Date().toISOString();
    const hasMedia = getMediaType(message.payloadType, message.attachments);

    analyticsService.track(ANALYTICS_EVENTS.MessageReceived, {
      message_id: message.msgId,
      channel: message.channel,
      has_media: Boolean(hasMedia),
      message_received_at: receivedAt,
    });
  },
  clearMessageMeta: (messageId: string) => {
    messageMeta.delete(messageId);
    trackedEvents.delete(messageId);
  },
  classifyEvent: (status: string) => {
    const normalized = status.toUpperCase();

    const sentStatuses = new Set<string>([
      ChatMessageStatus.SENT,
      ChatMessageStatus.QUEUED,
      ChatMessageStatus.SENDING,
    ]);
    if (sentStatuses.has(normalized)) {
      return 'sent';
    }

    const deliveredStatuses = new Set<string>([ChatMessageStatus.DELIVERED, ChatMessageStatus.READ]);
    if (deliveredStatuses.has(normalized)) {
      return 'delivered';
    }

    const failedStatuses = new Set<string>([
      ChatMessageStatus.UNDELIVERED,
      ChatMessageStatus.FAILED,
      ChatMessageStatus.ERROR,
      ChatMessageStatus.REJECTED,
      ChatMessageStatus.NOT_WHITELISTED,
      ChatMessageStatus.BLOCKED_NOT_IN_PACKAGE,
      ChatMessageStatus.BLOCKED_OVER_CREDIT_LIMIT,
      ChatMessageStatus.OPT_OUT,
      ChatMessageStatus.DEMO_LIMIT_EXCEEDED,
      'NOT_IN_PACKAGE',
      'UNKNOWN',
      'SENT_UNDELIVERED',
      'SPAM',
      'CALLED',
    ]);

    if (failedStatuses.has(normalized)) {
      return 'failed';
    }

    return null;
  },
};
